import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLang } from '@/hooks/useLang';
import LangSwitcher from '@/components/feature/LangSwitcher';
import { useDarkMode } from '@/hooks/useDarkMode';

interface NavbarProps {
  onRiskModal: () => void;
}

export default function Navbar({ onRiskModal }: NavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const { t, isRTL } = useLang();
  const { isDark, toggle: toggleDark } = useDarkMode();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === '/';

  const navLinksData = [
    { key: 'nav.rwaEntry', href: '#rwa-entry', icon: 'ri-seedling-line', color: '#34D399' },
    { key: 'nav.blockchain', href: '#blockchain', icon: 'ri-links-line', color: '#60A5FA' },
    { key: 'nav.policy', href: '#policy', icon: 'ri-global-line', color: '#A78BFA' },
    { key: 'nav.cases', href: '#cases', icon: 'ri-folder-chart-line', color: '#F59E0B' },
    { key: 'nav.hkInstitutions', href: '#hk-institutions', icon: 'ri-building-2-line', color: '#FB923C' },
    { key: 'nav.domestic', href: '#domestic', icon: 'ri-database-2-line', color: '#4ADE80' },
    { key: 'nav.landing', href: '#landing', icon: 'ri-route-line', color: '#F472B6' },
    { key: 'nav.industry', href: '#industry', icon: 'ri-grid-line', color: '#38BDF8' },
    { key: 'nav.education', href: '#education', icon: 'ri-graduation-cap-line', color: '#C084FC' },
    { key: 'nav.news', href: '#news', icon: 'ri-news-line', color: '#34D399' },
    { key: 'nav.glossary', href: '#glossary', icon: 'ri-book-2-line', color: '#F59E0B' },
    { key: 'nav.about', href: '#about', icon: 'ri-information-line', color: '#A78BFA' },
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const handleLogoClick = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <header
      dir={isRTL ? 'rtl' : 'ltr'}
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        scrolled
          ? isDark
            ? 'bg-gray-950/98 backdrop-blur-xl shadow-lg shadow-black/30 border-b border-gray-800/60'
            : 'bg-[#1A0050]/98 backdrop-blur-xl shadow-lg shadow-[#7C3AED]/15'
          : isDark
            ? 'bg-gray-900/95 backdrop-blur-md border-b border-gray-800/40'
            : 'bg-gradient-to-r from-[#1A0050] via-[#2E1065] to-[#1A0050]'
      }`}
    >
      {/* ─── Top Row: Logo + Controls ─── */}
      <div className="max-w-[1440px] mx-auto px-4 lg:px-6 py-2.5 flex items-center gap-3">
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          className="flex items-center gap-2.5 shrink-0 cursor-pointer group"
        >
          <img
            src="https://storage.readdy-site.link/project_files/314791f2-664e-475b-9351-85943b20c38c/736acc2c-7adc-4fbe-8b40-f4d6d3d31110_RWA99LOGO.jpg?v=7912c33faa018520798a6da21fca6851"
            alt="RWA99 Logo"
            className="w-8 h-8 rounded-lg object-cover shadow-md group-hover:shadow-[#7C3AED]/40 transition-shadow"
          />
          <div className="flex flex-col leading-none">
            <span className="text-white font-black text-base tracking-tight">
              RWA<span className="text-[#F59E0B]">99</span>
              <span className="text-white/40 text-[11px] font-normal">.cn</span>
            </span>
            <span className="text-white/40 text-[9px] font-medium hidden sm:block tracking-wide">
              {t('nav.tagline')}
            </span>
          </div>
        </button>

        {/* Home icon for secondary pages */}
        {!isHome && (
          <button
            onClick={() => navigate('/')}
            className="hidden xl:flex items-center gap-1.5 px-3 py-1.5 bg-white/8 border border-white/15 text-white/70 hover:text-white text-xs rounded-full hover:bg-white/12 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line text-[#F59E0B]"></i>
            {t('nav.home')}
          </button>
        )}

        <div className="flex-1" />

        {/* Right Controls */}
        <div className="flex items-center gap-1.5">
          {/* Search */}
          <div className="relative hidden md:block">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('nav.search')}
              className="bg-white/8 border border-white/12 text-white placeholder:text-white/35 rounded-full px-3.5 py-1.5 w-36 lg:w-44 text-xs focus:outline-none focus:border-[#F59E0B]/50 transition-all"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 flex items-center justify-center text-white/35">
              <i className="ri-search-line text-xs"></i>
            </div>
          </div>

          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDark}
            title={isDark ? '切换到浅色模式' : '切换到深色模式'}
            className="relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer focus:outline-none shrink-0"
            style={{ background: isDark ? '#7C3AED' : 'rgba(255,255,255,0.15)' }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow-sm transition-all duration-300"
              style={{ transform: isDark ? 'translateX(20px)' : 'translateX(0)' }}
            >
              <i className={`text-xs ${isDark ? 'ri-moon-fill text-[#7C3AED]' : 'ri-sun-line text-amber-500'}`}></i>
            </span>
          </button>

          {/* Language Switcher */}
          <LangSwitcher />

          {/* Column */}
          <button
            onClick={() => navigate('/column')}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 bg-white/8 border border-white/15 text-white text-xs font-bold rounded-full hover:bg-white/15 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-quill-pen-line text-xs text-[#F59E0B]" />
            {t('nav.column')}
          </button>

          {/* Forum */}
          <button
            onClick={() => navigate('/bbs')}
            className="hidden sm:flex items-center gap-1 px-2.5 py-1.5 bg-[#F59E0B]/12 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-bold rounded-full hover:bg-[#F59E0B]/22 transition-all whitespace-nowrap cursor-pointer"
          >
            <i className="ri-discuss-line text-xs" />
            {t('nav.forum')}
          </button>

          {/* Compliance Warning */}
          <button
            onClick={onRiskModal}
            className="px-3 py-1.5 bg-[#EF4444] text-white text-xs font-bold rounded-full hover:bg-red-500 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t('nav.risk')}
          </button>

          {/* Mobile hamburger */}
          <button
            className="xl:hidden w-7 h-7 flex items-center justify-center text-white cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* ─── Bottom Row: Navigation Items (Desktop Only) ─── */}
      <div className="hidden xl:block border-t border-white/[0.06]">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-6">
          <nav className="flex items-center justify-between">
            {navLinksData.map((link) => (
              <a
                key={link.key}
                href={isHome ? link.href : `/${link.href}`}
                onClick={!isHome ? (e) => { e.preventDefault(); navigate('/'); setTimeout(() => { const el = document.querySelector(link.href); if (el) el.scrollIntoView({ behavior: 'smooth' }); }, 100); } : undefined}
                className="group relative flex flex-col items-center gap-0.5 px-2 py-2 text-white/55 hover:text-white transition-all duration-200 cursor-pointer"
              >
                <div className="flex items-center gap-1">
                  <i className={`${link.icon} text-[10px]`} style={{ color: link.color }}></i>
                  <span className="text-[11px] font-medium whitespace-nowrap leading-none">{t(link.key)}</span>
                </div>
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] w-0 group-hover:w-3/4 transition-all duration-200 rounded-full"
                  style={{ background: link.color }}
                />
              </a>
            ))}
          </nav>
        </div>
      </div>

      {/* ─── Mobile Menu ─── */}
      {mobileOpen && (
        <div className={`xl:hidden border-t px-4 py-4 ${isDark ? 'bg-gray-950/98 border-gray-800' : 'bg-[#120038]/98 border-white/10'}`}>
          <div className="mb-3">
            <input
              type="text"
              placeholder={t('nav.search')}
              className={`w-full border rounded-full px-4 py-2 text-sm focus:outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder:text-gray-500' : 'bg-white/8 border-white/15 text-white placeholder:text-white/35'}`}
            />
          </div>
          <div className="grid grid-cols-3 gap-1.5 mb-4">
            {navLinksData.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className={`flex items-center gap-1.5 text-xs py-2 px-2.5 rounded-xl transition-colors cursor-pointer ${isDark ? 'text-gray-400 hover:text-[#F59E0B] hover:bg-gray-800' : 'text-white/65 hover:text-[#F59E0B] hover:bg-white/8'}`}
                onClick={() => setMobileOpen(false)}
              >
                <i className={`${link.icon} text-sm`} style={{ color: link.color }}></i>
                {t(link.key)}
              </a>
            ))}
          </div>
          <div className={`pt-3 border-t flex gap-2 flex-wrap items-center ${isDark ? 'border-gray-800' : 'border-white/10'}`}>
            <button
              onClick={() => { navigate('/'); setMobileOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/8 border border-white/15 text-white text-xs font-bold rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line text-[#F59E0B]"></i>
              {t('nav.home')}
            </button>
            <button
              onClick={() => { navigate('/column'); setMobileOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-white/8 border border-white/15 text-white text-xs font-bold rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-quill-pen-line text-[#F59E0B]"></i>
              {t('nav.column')}
            </button>
            <button
              onClick={() => { navigate('/bbs'); setMobileOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#F59E0B]/12 border border-[#F59E0B]/30 text-[#F59E0B] text-xs font-bold rounded-xl cursor-pointer whitespace-nowrap"
            >
              <i className="ri-discuss-line"></i>
              {t('nav.forum')}
            </button>
            <LangSwitcher compact />
          </div>
        </div>
      )}
    </header>
  );
}
